/**
 * Client Portal - My Contracts
 * Lists all contracts for the logged-in external user
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../../utils/apiClient';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { getStatusColor } from '../../utils/statusColors';
import { formatDate } from '../../utils/formatters';
import useCurrencyFormatter from '../../hooks/useCurrencyFormatter';
import useAsyncData from '../../hooks/useAsyncData';
import ContractReferenceCell from '../../components/contracts/ContractReferenceCell';
import SortableHeaderCell from '../../components/common/SortableHeaderCell';
import useTableSort from '../../hooks/useTableSort';

const CLIENT_CONTRACT_COLUMNS = [
  { key: 'reference',      label: 'Reference',      align: 'left',  getValue: (c) => c.reference },
  { key: 'product_type',   label: 'Product Type',   align: 'left',  getValue: (c) => c.product_type },
  { key: 'capital_amount', label: 'Capital Amount', align: 'right', getValue: (c) => c.capital_amount != null ? parseFloat(c.capital_amount) : null },
  { key: 'interest_rate',  label: 'Interest Rate',  align: 'right', getValue: (c) => c.interest_rate != null ? parseFloat(c.interest_rate) : null },
  { key: 'rate_type',      label: 'Rate Type',      align: 'left',  getValue: (c) => c.rate_type },
  { key: 'deposit_date',   label: 'Start Date',     align: 'left',  getValue: (c) => c.deposit_date },
  { key: 'maturity_date',  label: 'Maturity Date',  align: 'left',  getValue: (c) => c.maturity_date },
  { key: 'status',         label: 'Status',         align: 'left',  getValue: (c) => c.status },
];

const ClientContracts = () => {
  const sort = useTableSort();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrencyFormatter();
  const { data: contracts, loading, error } = useAsyncData(
    () => apiGet('/client-portal/my-contracts'),
    { defaultValue: [] }
  );

  const [searchTerm, setSearchTerm] = useState('');

  const filteredContracts = contracts.filter(contract =>
    contract.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contract.product_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contract.status?.toLowerCase().includes(searchTerm.toLowerCase())
  );


  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        My Contracts
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 2 }}>
          <TextField
            placeholder="Search contracts..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {CLIENT_CONTRACT_COLUMNS.map((col) => (
                  <SortableHeaderCell key={col.key} sort={sort} columnKey={col.key} align={col.align}>
                    {col.label}
                  </SortableHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredContracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      {searchTerm ? 'No contracts match your search' : 'No contracts found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sort.sortRows(filteredContracts, CLIENT_CONTRACT_COLUMNS).map((contract) => (
                  <TableRow
                    key={contract.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/client-portal/contracts/${contract.id}`)}
                  >
                    <TableCell>
                      <ContractReferenceCell reference={contract.reference} userReference={contract.user_reference} variant="body1" />
                    </TableCell>
                    <TableCell>{contract.product_type || '-'}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(contract.capital_amount, contract.currency_code)}
                    </TableCell>
                    <TableCell align="right">{contract.interest_rate}%</TableCell>
                    <TableCell>{contract.rate_type || '-'}</TableCell>
                    <TableCell>{formatDate(contract.deposit_date)}</TableCell>
                    <TableCell>{formatDate(contract.maturity_date)}</TableCell>
                    <TableCell>
                      <Chip
                        label={contract.status}
                        size="small"
                        color={getStatusColor(contract.status)}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ClientContracts;
